/**
 * Support Chat Store — manages support session state with WS real-time updates.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { wsManager } from '../ws/WebSocketManager';
import * as api from '../api/client';

export interface SupportMessage {
  id: number;
  session_id: number;
  sender_type: 'user' | 'operator' | 'system';
  body: string;
  msg_type: string;
  created_at_utc: string;
  operator_id?: number | null;
  clientMsgId?: string;
  status?: 'sending' | 'sent' | 'failed';
}

export interface SupportSession {
  id: number;
  ticket_id: string;
  wa_id: string;
  channel: string;
  status: 'pending' | 'open' | 'closed';
  operator_id: number | null;
  subject: string | null;
  queue_position: number | null;
  created_at_utc: string;
  closed_at_utc: string | null;
  rating: number | null;
}

interface SupportState {
  session: SupportSession | null;
  messages: SupportMessage[];
  status: 'idle' | 'connecting' | 'active' | 'closed';
  operatorName: string | null;
  operatorTyping: boolean;
  unreadCount: number;

  // Actions
  openSession: (subject?: string) => Promise<void>;
  loadActiveSession: () => Promise<void>;
  sendMessage: (body: string) => Promise<void>;
  closeSession: () => Promise<void>;
  rateSession: (rating: number) => Promise<void>;
  clearSupport: () => void;

  // WS lifecycle
  _wsCleanups: (() => void)[];
  initWebSocket: () => void;
  cleanupWebSocket: () => void;
}

let _msgCounter = 0;

export const useSupportStore = create<SupportState>()(
  persist(
    (set, get) => ({
      session: null,
      messages: [],
      status: 'idle',
      operatorName: null,
      operatorTyping: false,
      unreadCount: 0,
      _wsCleanups: [],

      openSession: async (subject?) => {
        set({ status: 'connecting' });
        try {
          const result = await api.openSupportSession(subject);
          const messages = result.isExisting
            ? (await api.getActiveSupportSession()).messages
            : [];
          set({
            session: result.session,
            messages,
            status: result.session.status === 'open' ? 'active' : 'connecting',
          });
        } catch (e: any) {
          set({ status: 'idle' });
          throw e;
        }
      },

      loadActiveSession: async () => {
        try {
          const result = await api.getActiveSupportSession();
          if (result.session) {
            set({
              session: result.session,
              messages: result.messages || [],
              status: result.session.status === 'open' ? 'active' : result.session.status === 'pending' ? 'connecting' : 'closed',
            });
          } else {
            set({ session: null, messages: [], status: 'idle' });
          }
        } catch { /* ignore */ }
      },

      sendMessage: async (body: string) => {
        const { session } = get();
        if (!session) return;

        const clientMsgId = `support-${Date.now()}-${++_msgCounter}`;
        const optimistic: SupportMessage = {
          id: 0,
          session_id: session.id,
          sender_type: 'user',
          body,
          msg_type: 'text',
          created_at_utc: new Date().toISOString(),
          clientMsgId,
          status: 'sending',
        };

        set(s => ({ messages: [...s.messages, optimistic] }));

        try {
          const result = await api.sendSupportMessage(session.id, body);
          set(s => ({
            messages: s.messages.map(m =>
              m.clientMsgId === clientMsgId ? { ...result.message, clientMsgId, status: 'sent' as const } : m
            ),
          }));
        } catch {
          set(s => ({
            messages: s.messages.map(m =>
              m.clientMsgId === clientMsgId ? { ...m, status: 'failed' as const } : m
            ),
          }));
        }
      },

      closeSession: async () => {
        const { session } = get();
        if (!session) return;
        try {
          await api.closeSupportSession(session.id);
          set(s => ({
            session: s.session ? { ...s.session, status: 'closed' } : null,
            status: 'closed',
          }));
        } catch { /* ignore */ }
      },

      rateSession: async (rating: number) => {
        const { session } = get();
        if (!session) return;
        try {
          await api.rateSupportSession(session.id, rating);
          set(s => ({
            session: s.session ? { ...s.session, rating } : null,
          }));
        } catch { /* ignore */ }
      },

      clearSupport: () => {
        set({
          session: null,
          messages: [],
          status: 'idle',
          operatorName: null,
          operatorTyping: false,
          unreadCount: 0,
        });
      },

      initWebSocket: () => {
        const cleanups: (() => void)[] = [];

        // Support message from operator
        cleanups.push(wsManager.on('support_message', (data: any) => {
          const payload = data?.payload || data;
          if (!payload?.body) return;
          const msg: SupportMessage = {
            id: Date.now(),
            session_id: payload.sessionId,
            sender_type: payload.senderType || 'operator',
            body: payload.body,
            msg_type: 'text',
            created_at_utc: payload.ts || new Date().toISOString(),
          };
          set(s => ({
            messages: [...s.messages, msg],
            unreadCount: s.unreadCount + 1,
          }));
        }));

        // Session claimed by operator
        cleanups.push(wsManager.on('support_session_claimed', (data: any) => {
          const payload = data?.payload || data;
          set(s => ({
            session: s.session ? { ...s.session, status: 'open' } : null,
            status: 'active',
            operatorName: payload?.operatorName || null,
            messages: [...s.messages, {
              id: Date.now(),
              session_id: s.session?.id || 0,
              sender_type: 'system' as const,
              body: `${payload?.operatorName || 'An agent'} has joined the chat.`,
              msg_type: 'text',
              created_at_utc: new Date().toISOString(),
            }],
          }));
        }));

        // Session closed
        cleanups.push(wsManager.on('support_session_closed', (data: any) => {
          const payload = data?.payload || data;
          set(s => ({
            session: s.session ? { ...s.session, status: 'closed' } : null,
            status: 'closed',
            messages: [...s.messages, {
              id: Date.now(),
              session_id: s.session?.id || 0,
              sender_type: 'system' as const,
              body: `Session closed${payload?.reason ? ` (${payload.reason})` : ''}.`,
              msg_type: 'text',
              created_at_utc: new Date().toISOString(),
            }],
          }));
        }));

        // Queue position update
        cleanups.push(wsManager.on('support_queue_update', (data: any) => {
          const payload = data?.payload || data;
          if (payload?.position != null) {
            set(s => ({
              session: s.session ? { ...s.session, queue_position: payload.position } : null,
            }));
          }
        }));

        // Typing indicator
        cleanups.push(wsManager.on('support_typing', (data: any) => {
          const payload = data?.payload || data;
          set({ operatorTyping: !!payload?.isTyping });
          // Auto-clear after 5s
          setTimeout(() => set({ operatorTyping: false }), 5000);
        }));

        set({ _wsCleanups: cleanups });
      },

      cleanupWebSocket: () => {
        const { _wsCleanups } = get();
        for (const cleanup of _wsCleanups) {
          try { cleanup(); } catch { /* ignore */ }
        }
        set({ _wsCleanups: [] });
      },
    }),
    {
      name: 'support-chat-storage',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        session: state.session,
        messages: state.messages.slice(-100),
        status: state.status,
        operatorName: state.operatorName,
      }),
    }
  )
);
